import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, AlertTriangle, HelpCircle, ShieldAlert, BookOpen, GitCompare, UserPlus, CheckCircle2 } from "lucide-react";
import { api, errMsg, pollJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const EDIT_SUGGESTIONS = ["Add a section for working at height.", "Change the equipment to a mobile scaffold tower.", "Include emergency isolation procedures.", "Add controls for occupied premises.", "Make the language more formal.", "Add a supervisor sign-off section."];

export const AiEditPanel = ({ doc, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (text.trim().length < 3) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/ai-edit`, { instruction: text });
      const job = await pollJob(data.job_id);
      toast.success(job.change_summary || "AI edit applied");
      setText("");
      onDone();
    } catch (e) {
      toast.error(e.response ? errMsg(e) : e.message);
    }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid="ai-edit-panel">
      <p className="text-sm text-muted-foreground">Tell Saflr what to change. Only the affected sections are updated, and a new version is saved.</p>
      <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Add controls for occupied premises" data-testid="ai-edit-input" disabled={busy || !doc.can["doc.edit"]} />
      <div className="flex flex-wrap gap-1.5">{EDIT_SUGGESTIONS.map((s) => <button key={s} onClick={() => setText(s)} className="rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground">{s}</button>)}</div>
      <Button onClick={run} disabled={busy || !doc.can["doc.edit"]} className="w-full" data-testid="ai-edit-submit"><Sparkles className="mr-1.5 h-4 w-4" />{busy ? "Applying edit…" : "Apply AI edit · 1 credit"}</Button>
      {["approved", "issued"].includes(doc.status) && <p className="text-xs text-[#B87300]">Editing an approved/issued document starts a new revision in Draft.</p>}
    </div>
  );
};

const FlagList = ({ icon: Icon, title, items, tone }) => items?.length ? (
  <div><p className={`mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${tone}`}><Icon className="h-3.5 w-3.5" />{title} ({items.length})</p>
    <ul className="space-y-1.5 text-sm">{items.map((x, i) => <li key={i} className="rounded-lg bg-muted/60 px-3 py-2">{x}</li>)}</ul></div>
) : null;

export const FlagsPanel = ({ c }) => (
  <div className="space-y-5" data-testid="flags-panel">
    <FlagList icon={ShieldAlert} title="High-risk — competent review" items={c.high_risk_flags} tone="text-alert" />
    <FlagList icon={AlertTriangle} title="Assumptions to verify" items={c.assumptions} tone="text-[#B87300] dark:text-[#FFB733]" />
    <FlagList icon={HelpCircle} title="Missing information" items={c.missing_information} tone="text-[#B87300] dark:text-[#FFB733]" />
    <div><p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-health"><BookOpen className="h-3.5 w-3.5" />References (reviewed library)</p>
      <ul className="space-y-1 text-sm">{(c.legislation_references || []).map((r, i) => <li key={i}>{r.title} <span className="text-xs text-muted-foreground">· {r.type.replace("_", " ")}</span></li>)}</ul>
      {c.unverified_references_removed?.length > 0 && <p className="mt-2 text-xs text-muted-foreground">{c.unverified_references_removed.length} citation(s) removed — not in the reviewed library.</p>}
    </div>
  </div>
);

export const WorkflowPanel = ({ doc, me, members, reload }) => {
  const [sel, setSel] = useState([]);
  const assigned = new Set(doc.assigned || []);
  const acked = new Set((doc.acknowledgements || []).map((a) => a.user_id));
  const assign = async () => {
    try {
      await api.post(`/documents/${doc.id}/assign`, { user_ids: sel });
      toast.success("Assigned for acknowledgement");
      setSel([]);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6" data-testid="workflow-panel">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Approval trail</p>
        {(doc.approvals || []).length === 0 && <p className="text-sm text-muted-foreground">Not yet submitted for review.</p>}
        <ol className="space-y-3 border-l pl-4">{(doc.approvals || []).map((a, i) => (
          <li key={i} className="relative text-sm"><span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-health" /><p className="font-medium capitalize">{a.action} · {a.name}</p><p className="font-mono text-[11px] text-muted-foreground">{a.at.slice(0, 16).replace("T", " ")} · {a.role}{a.signature && ` · signed “${a.signature}”`}</p>{a.comment && <p className="mt-1 text-muted-foreground">{a.comment}</p>}</li>
        ))}</ol>
      </div>
      {doc.status === "issued" && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Acknowledgements</p>
          <ul className="space-y-1.5">{members.map((m) => (
            <li key={m.id} className="flex items-center gap-2 text-sm">
              {doc.can["doc.issue"] && !assigned.has(m.id) && <input type="checkbox" checked={sel.includes(m.id)} onChange={(e) => setSel(e.target.checked ? [...sel, m.id] : sel.filter((x) => x !== m.id))} data-testid={`assign-check-${m.id}`} />}
              <span className="flex-1">{m.name}{m.id === me.id && " (you)"}</span>
              {acked.has(m.id) ? <span className="flex items-center gap-1 text-xs text-health"><CheckCircle2 className="h-3.5 w-3.5" />Acknowledged</span> : assigned.has(m.id) ? <span className="text-xs text-[#B87300]">Pending</span> : null}
            </li>
          ))}</ul>
          {doc.can["doc.issue"] && <Button size="sm" variant="outline" className="mt-3" disabled={!sel.length} onClick={assign} data-testid="assign-submit"><UserPlus className="mr-1.5 h-4 w-4" />Request acknowledgement</Button>}
        </div>
      )}
    </div>
  );
};

export const VersionsPanel = ({ doc }) => {
  const [versions, setVersions] = useState([]);
  const [pick, setPick] = useState([]);
  const [diff, setDiff] = useState(null);
  useEffect(() => {
    api.get(`/documents/${doc.id}/versions`).then((r) => setVersions(r.data));
  }, [doc.updated_at, doc.id]);
  const compare = async () => {
    const [a, b] = [...pick].sort((x, y) => x - y);
    const { data } = await api.get(`/documents/${doc.id}/compare`, { params: { a, b } });
    setDiff(data);
  };
  const tone = { added: "text-health", removed: "text-alert", changed: "text-[#B87300]", unchanged: "text-muted-foreground" };
  return (
    <div className="space-y-4" data-testid="versions-panel">
      <ul className="space-y-2">{versions.map((v) => (
        <li key={v.seq} className="flex gap-2 rounded-lg border p-2.5 text-sm">
          <input type="checkbox" checked={pick.includes(v.seq)} onChange={(e) => setPick(e.target.checked ? [...pick, v.seq].slice(-2) : pick.filter((x) => x !== v.seq))} data-testid={`version-check-${v.seq}`} />
          <div className="min-w-0"><p className="font-mono text-xs">#{v.seq} · v{v.version} · {v.status}</p><p className="truncate">{v.change_summary}</p><p className="text-[11px] text-muted-foreground">{v.by} · {v.created_at.slice(0, 16).replace("T", " ")}</p></div>
        </li>
      ))}</ul>
      <Button size="sm" variant="outline" disabled={pick.length !== 2} onClick={compare} data-testid="compare-versions"><GitCompare className="mr-1.5 h-4 w-4" />Compare selected</Button>
      {diff && <ul className="space-y-1 rounded-lg border p-3 text-sm" data-testid="compare-result">{diff.sections.map((s) => <li key={s.id} className="flex justify-between gap-2"><span className="truncate">{s.heading}</span><span className={`text-xs capitalize ${tone[s.state]}`}>{s.state}</span></li>)}</ul>}
    </div>
  );
};
